// Helper function to format numbers (replicate Liquid's format-number)
function formatNumber(value) {
  if (!value) return ''
  return parseFloat(value).toString()
}

// SIZE GUIDE TABLE CUSTOM ELEMENT
class SizeGuideTableElement extends HTMLElement {
  connectedCallback() {
    const variantsData = this.dataset.variants
    if (!variantsData) return

    try {
      const variants = JSON.parse(variantsData)
      this.renderTable(variants)
    } catch (e) {
      console.error('Failed to parse variants data:', e)
    }
  }

  renderTable(variants) {
    const tbody = this.querySelector('#size-guide-tbody')
    if (!tbody) return

    tbody.innerHTML = ''

    variants.forEach(variant => {
      // Only render if at least one measurement exists
      if (!variant.bust_max && !variant.waist_max && !variant.hips_max) {
        return
      }

      const row = document.createElement('tr')
      
      // Size column
      const sizeCell = document.createElement('td')
      sizeCell.textContent = variant.size
      row.appendChild(sizeCell)

      // Bust column
      const bustCell = document.createElement('td')
      if (variant.bust_max) {
        bustCell.textContent = formatNumber(variant.bust_max)
      }
      row.appendChild(bustCell)

      // Waist column
      const waistCell = document.createElement('td')
      if (variant.waist_max) {
        waistCell.textContent = formatNumber(variant.waist_max)
      }
      row.appendChild(waistCell)

      // Hips column
      const hipsCell = document.createElement('td')
      if (variant.hips_max) {
        hipsCell.textContent = formatNumber(variant.hips_max)
      }
      row.appendChild(hipsCell)

      tbody.appendChild(row)
    })
  }
}

// BDN-993: Style Profile measurements for a GUEST are cached client-side
// under the 'bodyShapePreferences' localStorage key. A LOGGED-IN customer's
// measurements are never written back to localStorage though (see
// birdsnest-style-profile-preact's StyleProfileContext) - they live
// server-side (Klaviyo) and are fetched through this same Shopify app proxy
// endpoint. Cache the fetch at module scope so multiple drawer instances on
// one page (e.g. quick view + PDP) only hit the API once.
const STYLE_PROFILE_API_PATH = '/apps/api-proxy/v1/customers/style-profiles'
let styleProfileFetchPromise = null

function fetchServerMeasurements() {
  if (!styleProfileFetchPromise) {
    styleProfileFetchPromise = fetch(STYLE_PROFILE_API_PATH, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })
      .then(response => (response.ok ? response.json() : null))
      .then(data => (data && data.properties) || null)
      .catch(() => null)
  }
  return styleProfileFetchPromise
}

// MY MEASUREMENTS CUSTOM ELEMENT
// Highlights the customer's own measurements inside the brand's sizing guide table.
class MyMeasurementsTableElement extends HTMLElement {
  connectedCallback() {
    const bustIndex = parseInt(this.dataset.bustIndex, 10)
    const waistIndex = parseInt(this.dataset.waistIndex, 10)
    const hipsIndex = parseInt(this.dataset.hipsIndex, 10)

    if (bustIndex < 0 && waistIndex < 0 && hipsIndex < 0) return

    const tbody = this.querySelector('tbody')
    if (!tbody) return

    this.render(tbody, { bustIndex, waistIndex, hipsIndex })
  }

  async render(tbody, indexes) {
    let measurements = this.readLocalMeasurements()

    // Nothing cached locally - if a customer is logged in (window.shopifyCustomerEmail
    // is set in layout/theme.liquid), ask the Style Profile API directly.
    if (!measurements && window.shopifyCustomerEmail) {
      const properties = await fetchServerMeasurements()
      measurements = this.toMeasurements(properties)
    }

    if (!measurements) return

    this.insertMeasurementsRow(tbody, measurements, indexes)
  }

  readLocalMeasurements() {
    try {
      const stored = window.localStorage.getItem('bodyShapePreferences')
      return stored ? this.toMeasurements(JSON.parse(stored)) : null
    } catch (e) {
      console.warn('Error reading style profile measurements:', e)
      return null
    }
  }

  toMeasurements(data) {
    if (!data) return null

    const bust = parseFloat(data.profile_body_measurements_bust)
    const waist = parseFloat(data.profile_body_measurements_waist)
    const hips = parseFloat(data.profile_body_measurements_hips)

    if (!bust && !waist && !hips) return null

    return { bust, waist, hips }
  }

  insertMeasurementsRow(tbody, measurements, { bustIndex, waistIndex, hipsIndex }) {
    // Compare against whichever measurement the table actually has a column
    // for, preferring bust, so the row lands in the correct sorted position.
    let compareIndex = bustIndex
    let compareValue = measurements.bust
    if (!(compareValue > 0) || compareIndex < 0) {
      compareIndex = waistIndex
      compareValue = measurements.waist
    }
    if (!(compareValue > 0) || compareIndex < 0) {
      compareIndex = hipsIndex
      compareValue = measurements.hips
    }
    if (!(compareValue > 0) || compareIndex < 0) return

    const rows = Array.from(tbody.querySelectorAll('tr'))
    let insertBeforeRow = null

    for (const row of rows) {
      const cell = row.children[compareIndex]
      const cellValue = cell ? parseFloat(cell.textContent) : NaN
      if (!isNaN(cellValue) && compareValue < cellValue) {
        insertBeforeRow = row
        break
      }
    }

    const columnCount = rows[0] ? rows[0].children.length : Math.max(bustIndex, waistIndex, hipsIndex) + 1
    const newRow = document.createElement('tr')
    newRow.classList.add('is-highlight')

    for (let i = 0; i < columnCount; i++) {
      const cell = document.createElement('td')
      if (i === 0) {
        cell.textContent = this.dataset.label || 'My measurements'
      } else if (i === bustIndex && measurements.bust > 0) {
        cell.textContent = formatNumber(measurements.bust)
      } else if (i === waistIndex && measurements.waist > 0) {
        cell.textContent = formatNumber(measurements.waist)
      } else if (i === hipsIndex && measurements.hips > 0) {
        cell.textContent = formatNumber(measurements.hips)
      }
      newRow.appendChild(cell)
    }

    if (insertBeforeRow) {
      tbody.insertBefore(newRow, insertBeforeRow)
    } else {
      tbody.appendChild(newRow)
    }
  }
}

// GARMENT MEASUREMENTS CUSTOM ELEMENT
class GarmentMeasurementsElement extends HTMLElement {
  constructor() {
    super()
    this.variants = []
    this.measurementMap = {
      'A': { label: 'Bust', field: 'bust_min', className: 'bust' },
      'B': { label: 'Waist', field: 'waist_min', className: 'waist' },
      'C': { label: 'Unfound', field: '', className: 'unfound measurement-data-container-unfoundC' },
      'D': { label: 'Hip', field: 'hip_fullest_part_min', className: 'hip' },
      'E': { label: 'Length - shoulder to hem', field: 'length_shoulder_to_hem_min', className: 'shoulder-to-hem' },
      'F': { label: 'Length - waist to hem', field: 'length_waist_to_hem_min', className: 'waist-to-hem' },
      'G': { label: 'Pants - inside leg', field: 'pants_inside_leg_crotch_to_hem_min', className: 'pants-inside' },
      'H': { label: 'Front - Rise Waist to Crotch', field: 'front_rise_waist_to_crotch_min', className: 'front-rise-waist' },
      'I': { label: 'Unfound', field: '', className: 'unfound measurement-data-container-unfoundI' },
      'J': { label: 'Sleeve length - neck to hem', field: 'sleeve_length_neck_to_hem_min', className: 'sleeve' }
    }
    // Container positions for overlay (in pixels) - applied to parent element
    this.containerPositions = {
      'A': { top: '90px', left: '102px' },
      'B': { top: '-5px', left: '50px' },
      'C': { top: '0px', left: '0px' },
      'D': { top: '49px', left: '26px' },
      'E': { top: '200px', left: '55px' },
      'F': { top: '19px', left: '115px' },
      'G': { top: '30px', left: '180px' },
      'H': { top: '37px', left: '58px' },
      'I': { top: '0px', left: '0px' },
      'J': { top: '5px', left: '185px' }
    }
  }

  connectedCallback() {
    // Parse variant data from data attribute
    const variantsData = this.dataset.variants
    if (variantsData) {
      try {
        this.variants = JSON.parse(variantsData)
      } catch (e) {
        console.error('Failed to parse variants data:', e)
        this.variants = []
      }
    }

    // Get elements
    this.select = this.querySelector('#measurement-size')
    this.overlay = this.querySelector('#measurement-overlay')
    this.list = this.querySelector('#measurement-list')

    if (!this.select || !this.overlay || !this.list) {
      return
    }

    // Populate select options
    this.populateSelect()

    // Bind event listener
    this.handleChange = () => this.renderMeasurements()
    this.select.addEventListener('change', this.handleChange)

    // Initial render
    this.renderMeasurements()
  }

  populateSelect() {
    this.select.innerHTML = ''

    // Sort variants by ordering_value_position (works with workaround in product-form liquid file for the PDP)
    const sortedVariants = [...this.variants].sort((a, b) => {
      const posA = parseInt(a.ordering_value_position, 10) || 0
      const posB = parseInt(b.ordering_value_position, 10) || 0
      return posA - posB
    })

    sortedVariants.forEach(variant => {
      const option = document.createElement('option')
      option.value = variant.size
      option.textContent = variant.size
      this.select.appendChild(option)
    })
  }

  renderMeasurements() {
    if (!this.select || !this.overlay || !this.list) {
      return
    }

    const selectedSize = this.select.value
    const variant = this.variants.find(v => v.size === selectedSize)

    if (!variant) {
      return
    }

    // Clear previous content
    this.overlay.innerHTML = ''
    this.list.innerHTML = ''

    // Render measurements
    Object.entries(this.measurementMap).forEach(([key, config]) => {
      const value = variant[config.field]
      const position = this.containerPositions[key]
      
      // Check if value exists and is not empty
      const hasValue = value && value !== '' && value !== 'null' && value !== 'undefined'
      const displayValue = hasValue ? formatNumber(value) : '0'
      const displayClass = hasValue ? '' : 'hidden'
      
      // Render overlay container with new structure
      if (position) {
        this.overlay.insertAdjacentHTML('beforeend', `
          <!-- ${key} - ${config.label} -->
          <div class="measurement-data-container measurement-data-container-${config.className} ${displayClass}">
            <div class="measurement-key">${key}</div>
            <div class="measurement-data-value">
              <span class="measurement-data-value-content">${displayValue}cm</span>
            </div>
          </div>
        `)
      }

      // Render list item only if has value
      if (hasValue) {
        this.list.insertAdjacentHTML('beforeend', `
          <li>
            <div class="measurement-key">${key}</div>
            <div>
              <p>${config.label}</p>
              <p>${formatNumber(value)}cm</p>
            </div>
          </li>
        `)
      }
    })
  }

  disconnectedCallback() {
    // Clean up event listener
    if (this.select && this.handleChange) {
      this.select.removeEventListener('change', this.handleChange)
    }
  }
}

// Register custom elements
if (!customElements.get('size-guide-table-element')) {
  customElements.define('size-guide-table-element', SizeGuideTableElement)
}

if (!customElements.get('garment-measurements-element')) {
  customElements.define('garment-measurements-element', GarmentMeasurementsElement)
}

if (!customElements.get('my-measurements-table')) {
  customElements.define('my-measurements-table', MyMeasurementsTableElement)
}
